import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { LucideAngularModule, ChevronDown, MicOff, VideoOff } from 'lucide-angular';
import { SalleAppel } from '../../../core/services/salle-appel.service';
import { PhotoAppelComponent } from './photo-appel.component';
import { TranslationService } from '../../../core/services/translation.service';
import { fondTuile } from './avatars-appel';
import { BarreControlesAppelComponent } from './barre-controles-appel.component';
import { PisteVideoComponent } from './piste-video.component';
import { TuileAppel } from './tuiles-appel';

/**
 * Appel a trois ou plus — maquette `GroupGrid` (Ecrans d'appel Konbit POS v2).
 *
 * Grille a deux colonnes ; une tuile seule sur sa derniere ligne occupe toute la largeur.
 * Au-dela de quatre tuiles la grille defile au lieu de les ecraser.
 *
 * ABSENT DE LA MAQUETTE, VOLONTAIREMENT : le bouton « Reduire ce cadre » de chaque tuile
 * (rien ne le fait fonctionner), le menu « Plus d'options » et la ligne « Caisse principale ».
 */
@Component({
  selector: 'app-grille-appel-groupe',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LucideAngularModule, BarreControlesAppelComponent, PisteVideoComponent, PhotoAppelComponent],
  template: `
    <div class="fixed inset-0 z-50 overflow-hidden text-surface-900 dark:text-[#f4f4fe] bg-gradient-to-b from-white to-surface-50
                dark:bg-[radial-gradient(130%_95%_at_15%_-10%,#332a86_0%,#1c1550_34%,#0c0930_62%,#020103_100%)]"
         role="dialog" aria-modal="true" [attr.aria-label]="t().calls.group_call">
      <div class="lueur-haute absolute pointer-events-none hidden dark:block" aria-hidden="true"></div>
      <div class="lueur-laterale absolute pointer-events-none hidden dark:block" aria-hidden="true"></div>

      <div class="relative z-[1] h-full flex flex-col">
        <div class="flex-none px-[18px] pt-5 flex items-center justify-between">
          <button type="button" (click)="minimiser.emit()" [attr.aria-label]="t().calls.minimize" class="rond border bg-white border-surface-200 text-surface-700
                  dark:bg-white/[0.09] dark:border-white/[0.08] dark:text-[#f4f4fe] dark:backdrop-blur-[10px]">
            <lucide-icon [img]="ChevronDown" [size]="20" aria-hidden="true"></lucide-icon>
          </button>
          <div class="flex items-center gap-2 rounded-full py-[7px] pl-[11px] pr-3.5 border bg-white border-surface-200 dark:bg-white/[0.07] dark:border-white/[0.08] dark:backdrop-blur-[10px]">
            <span class="w-[7px] h-[7px] rounded-full" [style.background]="salle.etat() === 'connectee' ? '#16a34a' : '#fbbf24'" aria-hidden="true"></span>
            <span class="text-xs font-bold tracking-[0.03em] tabular-nums text-surface-700 dark:text-[#e7e5fb]">{{ duree() }}</span>
          </div>
          <span class="w-11" aria-hidden="true"></span>
        </div>

        <div class="flex-none text-center px-6 pt-3">
          <div class="text-lg leading-[normal] font-extrabold tracking-[-0.01em]">{{ t().calls.group_call }}</div>
          <div class="text-[12.5px] leading-[normal] mt-0.5 text-surface-500 dark:text-[#a5a1d6]" aria-live="polite">{{ sousTitre() }}</div>
        </div>

        @if (message(); as m) {
          <div class="mx-5 mt-2 rounded-xl px-3.5 py-2 text-[12.5px] flex items-center justify-between gap-3 border bg-accent-50 border-accent-200 text-accent-800
                      dark:bg-[rgba(251,191,36,0.14)] dark:border-[rgba(251,191,36,0.3)] dark:text-[#fde68a]" role="alert">
            <span>{{ m }}</span>
            @if (salle.etat() === 'coupee' || salle.etat() === 'erreur') {
              <button type="button" (click)="salle.reessayer()" class="font-bold underline min-h-[44px]">{{ t().calls.retry }}</button>
            }
          </div>
        }

        <div class="flex-1 min-h-0 px-4 pt-3.5 pb-1.5">
          <div class="h-full grid grid-cols-2 gap-2 content-start" [class.defile]="tuiles().length > 4"
               [style.grid-template-rows]="tuiles().length <= 4 ? 'repeat(' + lignes() + ',minmax(0,1fr))' : null"
               [style.grid-auto-rows]="tuiles().length > 4 ? '168px' : null">
            @for (tuile of tuiles(); track tuile.identity; let i = $index) {
              <div class="tuile relative rounded-[20px] overflow-hidden flex items-center justify-center border"
                   [class.parle]="tuile.parle" [class.tuile-moi]="tuile.moi" [class.col-span-2]="derniereSeule(i)"
                   [class]="tuile.moi ? 'bg-primary-50 border-primary-200 dark:border-transparent dark:bg-[linear-gradient(160deg,#3730a3_0%,#1e1b4b_60%,#0c0a22_100%)]'
                                      : 'bg-surface-100 border-surface-200 dark:border-transparent'"
                   [style.--fond-sombre]="tuile.moi ? null : fond(tuile)">
                @if (tuile.videoTrack) {
                  <app-piste-video [piste]="tuile.videoTrack" [miroir]="tuile.moi" class="absolute inset-0" />
                } @else {
                  <div class="flex flex-col items-center gap-1.5">
                    <div class="avatar-tuile w-[52px] h-[52px] rounded-full overflow-hidden flex items-center justify-center text-lg font-bold border bg-white text-surface-700
                                dark:border-0 dark:text-white"
                         [class]="tuile.moi ? 'border-primary-200 text-primary-800' : 'border-surface-200'" [style.--degrade]="tuile.degrade"><app-photo-appel [photo]="tuile.photo" [nom]="tuile.nom" [initiales]="tuile.initiale" /></div>
                    @if (tuile.moi) {
                      <span class="flex items-center gap-1 text-[11px] font-semibold text-surface-600 dark:text-[#c7c3f5]">
                        <lucide-icon [img]="VideoOff" [size]="13" aria-hidden="true"></lucide-icon>{{ t().calls.camera_off }}
                      </span>
                    }
                  </div>
                }
                <div class="absolute bottom-2 left-2 flex items-center gap-[5px] rounded-full py-1 pl-[7px] pr-[10px] border bg-white/95 dark:border-0 dark:backdrop-blur-[8px]"
                     [class]="tuile.moi ? 'border-primary-200 dark:bg-[rgba(79,70,229,0.55)]' : 'border-surface-200 dark:bg-[rgba(5,4,13,0.55)]'">
                  @if (!tuile.micro) {
                    <lucide-icon [img]="MicOff" [size]="13" class="text-danger-600 dark:text-[#fb7185]" [attr.aria-label]="t().calls.muted"></lucide-icon>
                  }
                  <span class="text-[11.5px]" [class.font-bold]="tuile.moi" [class.font-semibold]="!tuile.moi">{{ tuile.moi ? t().calls.you : tuile.nom }}</span>
                </div>
              </div>
            }
          </div>
        </div>

        <app-barre-controles-appel (ajouter)="ajouter.emit()" (raccrocher)="raccrocher.emit()" />
      </div>
    </div>
  `,
  styles: [
    `
      .rond { width:44px; height:44px; border-radius:9999px; display:flex; align-items:center; justify-content:center; cursor:pointer; }
      :host-context(.dark) .avatar-tuile { background:var(--degrade); }
      :host-context(.dark) .tuile:not(.tuile-moi) { background:var(--fond-sombre); }
      .defile { overflow-y:auto; }
      .tuile.parle { border-color:#4f46e5; border-width:3px; }
      :host-context(.dark) .tuile.parle { border-width:1px; border-color:transparent; animation:cercle-parole 2.2s ease-in-out infinite; }
      .lueur-haute { top:-130px; right:-100px; width:360px; height:360px; border-radius:9999px;
        background:radial-gradient(circle,rgba(129,140,248,0.34) 0%,rgba(129,140,248,0) 70%); filter:blur(4px); animation:derive-1 16s ease-in-out infinite; }
      .lueur-laterale { top:220px; left:-120px; width:300px; height:300px; border-radius:9999px;
        background:radial-gradient(circle,rgba(56,189,248,0.16) 0%,rgba(56,189,248,0) 70%); filter:blur(6px); animation:derive-2 13s ease-in-out infinite; }
      @keyframes cercle-parole {
        0%,100% { box-shadow:0 0 0 0 rgba(74,222,128,0.55),0 0 0 1px rgba(255,255,255,0.10) inset; }
        50% { box-shadow:0 0 0 3px rgba(74,222,128,0.28),0 0 0 1px rgba(255,255,255,0.10) inset; }
      }
      @keyframes derive-1 { 0%,100% { transform:translate(0,0) scale(1); } 50% { transform:translate(-18px,14px) scale(1.06); } }
      @keyframes derive-2 { 0%,100% { transform:translate(0,0); } 50% { transform:translate(16px,-10px); } }
      @media (prefers-reduced-motion: reduce) { .lueur-haute, .lueur-laterale, .tuile.parle { animation:none; } .tuile.parle { box-shadow:0 0 0 3px rgba(74,222,128,0.55); } }
    `,
  ],
})
export class GrilleAppelGroupeComponent {
  readonly duree = input.required<string>();
  /** Tous les participants de la salle, soi-meme en tete (voir `construireTuiles`). */
  readonly tuiles = input.required<TuileAppel[]>();

  readonly minimiser = output<void>();
  readonly ajouter = output<void>();
  readonly raccrocher = output<void>();

  readonly salle = inject(SalleAppel);
  readonly t = inject(TranslationService).t;

  readonly ChevronDown = ChevronDown;
  readonly MicOff = MicOff;
  readonly VideoOff = VideoOff;

  readonly lignes = computed(() => Math.max(1, Math.ceil(this.tuiles().length / 2)));

  readonly sousTitre = computed(() => {
    const c = this.t().calls;
    if (this.salle.etat() === 'connexion') return c.connecting;
    if (this.salle.etat() === 'reconnexion') return c.reconnecting;
    const n = this.tuiles().length;
    return (n === 1 ? c.participants_one : c.participants_many).replace('{n}', String(n));
  });

  readonly message = computed(() => {
    const c = this.t().calls;
    if (this.salle.etat() === 'coupee') return c.connection_lost;
    if (this.salle.etat() === 'erreur') return c.connection_error;
    const a = this.salle.avertissement();
    return a === 'micro_refuse' ? c.mic_denied : a === 'camera_refusee' ? c.camera_denied : null;
  });

  fond(tuile: TuileAppel): string {
    return fondTuile(tuile.nom);
  }

  /** Une tuile seule sur la derniere ligne occupe toute la largeur. */
  derniereSeule(index: number): boolean {
    const n = this.tuiles().length;
    return n % 2 === 1 && index === n - 1 && n > 1;
  }
}
